import METADATA from 'containers/MarketPage/metadata';
import { getRecommendedMetadata } from 'containers/MarketPage/metadata/recommended';
import React, { FC, memo, useMemo } from 'react';
import Helmet from 'react-helmet';
import renderWithWebDomain from 'utils/renderWithWebDomain';
import { Category, Tag } from '../../types/schema';
import { generateSlugFromID } from '../../utils/urls/categoriesPathSegment';

export interface IMetadataKeyData {
  data: string | number;
  type: 'category' | 'tags' | 'page' | 'empty' | 'search' | 'recommended';
  querySearch?: string;
  metaFromDescription?: string;
  businessCategory?: string;
}

interface RenderMetadataMarketProps {
  metadataKey: IMetadataKeyData;
  page?: number;
  noIndex?: boolean;
  breadCrumbData?: string;
  localLang: string;
  alternateLang: string;
  category: Category;
  productTags: Tag[];
}

const RenderMetadataMarket: FC<RenderMetadataMarketProps> = (props) => {
  const {
    metadataKey: { data, type, querySearch, businessCategory },
    page,
    breadCrumbData,
    localLang,
    alternateLang,
    category,
    productTags,
  } = props;

  const marketPageUrls = useMemo(() => {
    const pageQuery = Number(page) > 1 ? `page=${page}` : '';
    const queryResult = !!pageQuery ? `?${pageQuery}` : '';

    switch (type) {
      case 'category':
        return {
          validURL: `/${generateSlugFromID(category, localLang)}${queryResult}`,
          alternateLangURL: `/${generateSlugFromID(category, alternateLang)}${queryResult}`,
          default: `/${generateSlugFromID(category, 'vi')}${queryResult}`,
        };
      case 'tags':
        return {
          validURL: `/${(data as string).split(' ').join('-')}${queryResult}`,
        };
      case 'page':
        return {
          validURL: `/market${queryResult}`,
        };
      case 'search':
        return {
          validURL: `/market?q=${encodeURIComponent(querySearch ?? '')}`,
        };
      default:
        return '';
    }
  }, [page, type, querySearch, data, category, localLang, alternateLang]);

  const { validURL = '', alternateLangURL = '', default: defaultLangURL = '' } = marketPageUrls || {};
  const defaultURL = defaultLangURL || validURL;
  const isOtherTags = type === 'tags' && METADATA[data] === undefined;

  const metadata = useMemo(() => {
    if (type === 'recommended') {
      const recommendedMetadata = getRecommendedMetadata(businessCategory || undefined);
      return recommendedMetadata[localLang || 'vi'];
    }

    if (METADATA[data]) {
      return METADATA[data][localLang || 'vi'];
    }

    if ((type === 'category' && category.nameLocal) || isOtherTags) {
      return METADATA.DEFAULT[localLang || 'vi'];
    }

    return undefined;
  }, [type, businessCategory, localLang, data, category.nameLocal, isOtherTags]);

  const currentTag = isOtherTags && data ? productTags.find((tag) => tag.id === data) : '';

  const title = (() => {
    if (!metadata) return '';
    if (type === 'recommended') {
      return metadata.title;
    }
    if (data === 'search') {
      return metadata.generateTitle?.(querySearch ?? '');
    }
    if (metadata.title !== 'Tag') {
      return metadata.title;
    }
    if (type === 'category' && category.nameLocal) {
      return metadata.generateTitle?.(category.nameLocal);
    }
    if (isOtherTags && currentTag) {
      const tagName = localLang === 'vi' ? currentTag.nameVn : currentTag.nameEn;
      return metadata.generateTitle?.(tagName);
    }
    return metadata.title;
  })();

  const description = (() => {
    if (!metadata) return '';
    if (type === 'recommended') {
      return metadata.description;
    }
    if (data === 'search') {
      return metadata.generateDescription?.(querySearch ?? '');
    }
    if (metadata.title !== 'Tag') {
      return metadata.description;
    }
    if (type === 'category' && category.nameLocal) {
      return metadata.generateDescription?.(category.nameLocal);
    }
    if (isOtherTags && currentTag) {
      const tagName = localLang === 'vi' ? currentTag.nameVn : currentTag.nameEn;
      return metadata.generateDescription?.(tagName);
    }
    return metadata.description;
  })();

  const imageUrl = metadata?.image?.url || '';
  const imageHeight = metadata?.image?.height || '';
  const imageWidth = metadata?.image?.width || '';
  const imageAlt = category?.name || data.toString();

  if (!data || !metadata) {
    return (
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta property="og:url" content={renderWithWebDomain(process.env.APP_ENV, validURL)} />
      <link rel="canonical" href={renderWithWebDomain(process.env.APP_ENV, defaultURL)} />
      <meta property="og:title" content={title} />

      {type === 'category' && (
        <link rel="alternate" hrefLang={localLang} href={renderWithWebDomain(process.env.APP_ENV, validURL)} />
      )}
      {type === 'category' && (
        <link
          rel="alternate"
          hrefLang={alternateLang}
          href={renderWithWebDomain(process.env.APP_ENV, alternateLangURL)}
        />
      )}
      {type === 'category' && (
        <link rel="alternate" hrefLang="x-default" href={renderWithWebDomain(process.env.APP_ENV, defaultURL)} />
      )}
      <meta name="description" content={description} />
      <meta name="og:description" content={description} />
      <meta property="og:image:url" content={renderWithWebDomain(process.env.APP_ENV, imageUrl)} />
      <meta property="og:image:width" content={imageHeight} />
      <meta property="og:image:height" content={imageWidth} />
      <meta property="og:image:secure_url" content={renderWithWebDomain(process.env.APP_ENV, imageUrl)} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="twitter:card" content="summary" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={renderWithWebDomain(process.env.APP_ENV, imageUrl)} />
      <meta property="twitter:image:alt" content={imageAlt} />
      {data === 'search' && (
        <link
          rel="alternate"
          hrefLang={localLang}
          href={`${renderWithWebDomain(process.env.APP_ENV, validURL)}${localLang === 'en' ? '&lang=en' : ''}`}
        />
      )}
      {data === 'search' && (
        <link
          rel="alternate"
          hrefLang={alternateLang}
          href={`${renderWithWebDomain(process.env.APP_ENV, validURL)}${alternateLang === 'en' ? '&lang=en' : ''}`}
        />
      )}
      {data === 'search' && (
        <link rel="alternate" hrefLang="x-default" href={renderWithWebDomain(process.env.APP_ENV, validURL)} />
      )}

      {/* {noIndex && <meta name="robots" content="noindex, nofollow" />} */}
      <script type="application/ld+json">{breadCrumbData ?? ''}</script>
    </Helmet>
  );
};

export default memo(RenderMetadataMarket);
